import { ReviewPost } from '../../types/review';

const review: ReviewPost = {
  id: 1010,
  slug: 'mylovely-ai-review',
  title: 'MyLovely.ai Review: Realistic Roleplay Chat and Prompt-Accurate Images',
  excerpt:
    'MyLovely.ai is an AI girlfriend platform with character stories, chat, image generation, and video. In this test the chat stayed in character and the image followed the prompt. The layout was the weak point, and phone calls are not available yet.',
  content: `
    <div class="table-of-contents bg-purple-950/30 border border-pink-500/20 p-6 rounded-lg mb-8">
      <h2 class="text-2xl font-bold text-pink-400 mb-4" id="toc-title">Table of Contents</h2>
      <ul class="space-y-2">
        <li class="text-pink-200"><a href="#what-is-mylovely" class="hover:text-pink-400 transition-colors">What Is MyLovely.ai?</a></li>
        <li class="text-pink-200"><a href="#features" class="hover:text-pink-400 transition-colors">Features</a></li>
        <li class="text-pink-200"><a href="#what-we-liked" class="hover:text-pink-400 transition-colors">What We Liked</a></li>
        <li class="text-pink-200"><a href="#what-we-didnt-like" class="hover:text-pink-400 transition-colors">What We Didn't Like</a></li>
        <li class="text-pink-200"><a href="#best-for" class="hover:text-pink-400 transition-colors">What MyLovely.ai Is Best For</a></li>
        <li class="text-pink-200"><a href="#what-stands-out" class="hover:text-pink-400 transition-colors">What Stands Out</a></li>
        <li class="text-pink-200"><a href="#pricing" class="hover:text-pink-400 transition-colors">Pricing</a></li>
        <li class="text-pink-200"><a href="#still-needs-testing" class="hover:text-pink-400 transition-colors">What We Still Need to Check</a></li>
        <li class="text-pink-200"><a href="#verdict" class="hover:text-pink-400 transition-colors">Early Verdict</a></li>
        <li class="text-pink-200"><a href="#faq" class="hover:text-pink-400 transition-colors">MyLovely.ai FAQ</a></li>
      </ul>
    </div>

    <p id="what-is-mylovely" class="mb-6">
      MyLovely.ai is an <a href="https://heavengirlfriend.com/best-ai-girlfriend" class="text-pink-400 hover:text-pink-300 transition-colors">AI girlfriend</a> platform built around character profiles, story scenarios, chat, image generation, and short video. After signup you land on a character page and pick a companion from a large roster. Profiles include a story, photos, and a verified badge on some characters.
    </p>

    <p class="mb-6">
      This test used Stacy Peterson. Her profile sets up a locked university sports storage room and writes her as an aggressive, foul-mouthed cheerleader who wants nothing to do with you at the start. The test also covered one custom image and one video made from that image. Phone calls were not available.
    </p>

    <h2 id="features" class="text-2xl font-bold text-pink-400 mb-4">Features</h2>
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li>A large character roster with different looks, ages, and story setups</li>
      <li>Character profiles with a biography, photos, and a verified badge</li>
      <li>Story-based text chat</li>
      <li>A typing indicator and an online badge</li>
      <li>Custom image prompts tied to the selected character</li>
      <li>Video generation from a generated photo</li>
    </ul>
    <p class="mb-6">
      Call features were marked as not available yet. This pass did not include a custom character builder.
    </p>

    <h2 id="what-we-liked" class="text-2xl font-bold text-pink-400 mb-4">What We Liked</h2>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Characters look good, and there are a lot of them</h3>
    <p class="mb-6">
      The roster has a wide mix of looks and setups, including different ages, styles, and an animated option. The profiles look polished. Stacy's storage-room story was specific enough to give the chat a direction instead of a blank greeting.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">The chat stays in character and pushes back</h3>
    <p class="mb-6">
      Stacy opened hostile, stayed foul-mouthed, and did not flip to affectionate just because the tester asked. She quoted a line back and treated it with sarcasm, which felt like a real reaction. The replies are written as a scene, with actions and emotion, so the story is easy to picture. Winning her over took actual replies. A lot of AI girlfriends agree too quickly. This one did not.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Typing indicator and online status</h3>
    <p class="mb-6">
      The typing box and the online badge make the wait feel more like a live chat, even though the messages themselves read like story prose rather than short texts.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Image generation followed the prompt</h3>
    <p class="mb-6">
      A custom prompt asked for Stacy dancing in the closet with an angry face. The image finished in about 10 seconds. She kept the same clothes and the same face, the setting was the closet, and the expression matched. It was semi-realistic rather than fully photographic, but it listened to the request. That prompt match is the hard part, and MyLovely.ai got it.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Video followed the photo</h3>
    <p class="mb-6">
      The video was generated from that image and used a dance the tester did not describe in detail. It took about 5 minutes. She danced, and the clothes and body moved with her. It was not fully realistic, and the motion still matched the picture.
    </p>

    <h2 id="what-we-didnt-like" class="text-2xl font-bold text-pink-400 mb-4">What We Didn't Like</h2>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Listed ages do not match the faces</h3>
    <p class="mb-6">
      Stacy is listed as 19 and looked older than that. Another character, listed as 29, looked much younger. The faces are attractive. The age labels are not reliable.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">The chat layout gets in the way</h3>
    <p class="mb-6">
      The interface felt off-proportion. On a normal window the full chat was not visible, and the tester had to minimize the screen to read it. That is a basic problem on a chat-first product.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Messages read like a story, not a text thread</h3>
    <p class="mb-6">
      The scene writing is a strength for roleplay and a weakness if you want short, casual texts. Replies describe the room, the body, and the action in long blocks. They are immersive. They are not how most people text.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">Visuals are only semi-realistic</h3>
    <p class="mb-6">
      Both the image and the video followed the request and still looked generated. More realism is the clear next step. The video also took about 5 minutes, which is slow next to the 10-second still.
    </p>

    <h3 class="text-xl font-bold text-pink-300 mb-3">No calls yet</h3>
    <p class="mb-6">
      Phone or voice calls were not available in this test. The product stops at text, images, and video.
    </p>

    <h2 id="best-for" class="text-2xl font-bold text-pink-400 mb-4">What MyLovely.ai Is Best For</h2>
    <p class="mb-6">MyLovely.ai fits people who want:</p>
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li>Story roleplay where the character does not agree with everything</li>
      <li>A large roster with written scenarios</li>
      <li>Images that keep the character's look and follow a custom prompt</li>
      <li>A short video based on a picture you just generated</li>
    </ul>
    <p class="mb-6">
      It is a weaker fit if you want a clean chat layout, short text-message replies, voice calls, or faces that match the listed age.
    </p>

    <h2 id="what-stands-out" class="text-2xl font-bold text-pink-400 mb-4">What Stands Out</h2>
    <p class="mb-6">
      The chat is the part that stood out. Stacy stayed difficult, the scene was easy to imagine, and the tester had to think about the next line. That resistance is rare on AI girlfriend apps.
    </p>
    <p class="mb-6">
      The image prompt is the other standout. Ten seconds, same person, same clothes, and the closet and the angry expression both landed. The video then moved that picture without breaking the pose request.
    </p>

    <h2 id="pricing" class="text-2xl font-bold text-pink-400 mb-4">Pricing</h2>
    <p class="mb-6">
      This test bought <strong>$20 in tokens</strong>, which came to about <strong>455 coins</strong>. The footage does not show how many coins one image or one video costs, or how the $20 purchase compares with a subscription.
    </p>

    <h2 id="still-needs-testing" class="text-2xl font-bold text-pink-400 mb-4">What We Still Need to Check</h2>
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li>Whether other characters push back the way Stacy did, or only this scenario does</li>
      <li>How often listed ages miss the face</li>
      <li>Whether the chat layout is usable on a normal laptop and on a phone</li>
      <li>Memory across a longer session and a second visit</li>
      <li>More image prompts, including ones that are less close to the profile photo</li>
      <li>Whether a 5-minute video is typical, and how many coins it costs</li>
      <li>How far 455 coins actually go</li>
      <li>When call features arrive, and whether they match the character</li>
    </ul>

    <h2 id="verdict" class="text-2xl font-bold text-pink-400 mb-4">Early Verdict</h2>
    <p class="mb-6">
      MyLovely.ai is worth trying if you want a character who stays difficult and an image tool that follows a specific prompt. The storage-room chat was engaging, the still matched the request, and the video carried that still into motion.
    </p>
    <p class="mb-6">
      It is not polished yet. The chat window got in the way, the writing is scene prose rather than texting, the faces do not match the listed ages, visuals are only semi-realistic, and calls are missing. One character and one prompt are not enough for a final rank, but this pass is stronger on chat than a generic companion app.
    </p>
    <p class="mb-6">On Heaven Girlfriend's 5-point scale, this video test updates the category scores to:</p>
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li><strong>Chat Quality:</strong> 3.6 out of 5</li>
      <li><strong>Image Quality:</strong> 3.5 out of 5</li>
      <li><strong>Generative Tools:</strong> 3.8 out of 5</li>
      <li><strong>Characters:</strong> 3.4 out of 5</li>
      <li><strong>User Experience:</strong> 2.5 out of 5</li>
    </ul>
    <p class="mb-6">
      With the rest of the published criteria, that lands at <strong>3.48 out of 5</strong>. Try it for story roleplay and prompt-accurate images. Do not expect a clean chat screen, voice calls, or fully realistic video yet.
    </p>

    <h2 id="faq" class="text-2xl font-bold text-pink-400 mb-4">MyLovely.ai FAQ</h2>
    <h3 class="text-xl font-bold text-pink-300 mb-3">What is MyLovely.ai?</h3>
    <p class="mb-6">
      MyLovely.ai is an AI girlfriend platform with a character roster, written scenarios, story chat, custom image prompts, and video made from those images.
    </p>
    <h3 class="text-xl font-bold text-pink-300 mb-3">Is MyLovely.ai chat any good?</h3>
    <p class="mb-6">
      In this test, yes. Stacy stayed hostile, quoted the tester's line back, and did not become easy to win over. The replies are long scene descriptions, and the chat window was hard to read until the screen was minimized.
    </p>
    <h3 class="text-xl font-bold text-pink-300 mb-3">Does MyLovely.ai image generation follow prompts?</h3>
    <p class="mb-6">
      The one custom prompt did. Stacy kept her clothes and face, appeared in the closet, and had an angry expression while dancing. It took about 10 seconds and looked semi-realistic.
    </p>
    <h3 class="text-xl font-bold text-pink-300 mb-3">How much did the MyLovely.ai test cost?</h3>
    <p class="mb-6">
      The tester bought $20 in tokens and received about 455 coins. The coin cost of a single image or video was not shown.
    </p>
  `,
  image: '/mylovely-ai-review-cover.webp',
  date: 'February 24, 2026',
  reviewDate: 'October 26, 2026',
  readTime: '8 min read',
  category: 'AI Companion',
  featured: false,
  authorSlug: 'michael-huynh',
  metaTitle: 'MyLovely.ai Review 2026: Chat, Images, and Video',
  metaDescription:
    'MyLovely.ai review 2026. Story chat stays in character, and a custom image followed the prompt in about 10 seconds. The chat layout is clumsy, and calls are not available yet.',
  faqs: [
    {
      question: 'What is MyLovely.ai?',
      answer:
        'MyLovely.ai is an AI girlfriend platform with a character roster, written scenarios, story chat, custom image prompts, and video made from those images.',
    },
    {
      question: 'Is MyLovely.ai chat any good?',
      answer:
        'In this test, yes. The character stayed hostile, quoted a line back, and did not become easy to win over. The replies are long scene descriptions, and the chat window was hard to read until the screen was minimized.',
    },
    {
      question: 'Does MyLovely.ai image generation follow prompts?',
      answer:
        'The one custom prompt did. The character kept her clothes and face, appeared in the requested closet, and had an angry expression. It took about 10 seconds and looked semi-realistic.',
    },
    {
      question: 'How much did the MyLovely.ai test cost?',
      answer:
        'The tester bought $20 in tokens and received about 455 coins. The coin cost of a single image or video was not shown.',
    },
  ],
  productName: 'MyLovely AI',
  rating: 3.48,
  ratingCategories: [
    { name: 'Chat Quality', score: 3.6 },
    { name: 'Image Quality', score: 3.5 },
    { name: 'Features', score: 3.5 },
    { name: 'Generative Tools', score: 3.8 },
    { name: 'User Experience', score: 2.5 },
    { name: 'Value/Money', score: 3.0 },
    { name: 'Characters', score: 3.4 },
    { name: 'Customization', score: 3.8 },
    { name: 'Advanced Content', score: 4.2 },
    { name: 'Free Trial', score: 3.8 },
  ],
  pros: [
    'Story chat stays in character and pushes back',
    'Typing indicator and online badge',
    'Custom image matched the character, closet, and expression in about 10 seconds',
    'Video carried that image into motion',
    'Large roster with specific scenarios',
  ],
  cons: [
    'Listed ages do not match the faces',
    'Chat layout hid part of the thread until the window was minimized',
    'Replies read like scene prose, not text messages',
    'Image and video are only semi-realistic',
    'Video took about 5 minutes',
    'Calls are not available yet',
  ],
  features: [
    'Character roster and story profiles',
    'Story-based text chat',
    'Typing indicator and online badge',
    'Custom image prompts',
    'Video from a generated photo',
  ],
  testimonials: [],
  affiliateLink: '/go/mylovely-ai',
  price: '$20 for about 455 coins',
  label: 'In-character roleplay and prompt-accurate images',
  verdict:
    'Strong story chat and an image prompt that followed the request. The layout, age labels, and semi-realistic video still hold it back.',
  bestFor: 'Story roleplay where the character resists, plus images that match a specific prompt.',
  keyPros: [
    'Chat that stays difficult and in character',
    'Image prompt followed the request',
    'Video motion matched the photo',
  ],
  keyCons: [
    'Chat window is hard to read',
    'Ages do not match the faces',
    'No calls yet',
  ],
  homePagePros: ['In-character story chat', 'Prompt-accurate images', 'Video from a photo'],
  homePageCons: ['Clumsy chat layout', 'Ages miss the faces', 'Calls not available'],
  rank: 18,
};

export default review;
